/**
 * LayoutSelectorPanel - Right slide-out panel for layout selection
 *
 * Design: Shadcn-inspired with modern aesthetics
 * - Slide-in animation from right
 * - Current layout preview at top
 * - Current template layouts first
 * - Other templates expandable below
 * - Smooth transitions and hover states
 */

import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { X, Check, Sparkles, LayoutGrid, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import type { SlideOutline, TemplateLayout } from '@/types/outline';
import type { ParentTemplate } from '@/types/template';
import { templateAPI } from '@/services/api';

interface LayoutSelectorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  slide: SlideOutline;
  conversationId: string;
  layouts: TemplateLayout[];
  onLayoutSelect: (layoutId: string, layoutName: string, thumbnail?: string) => void;
}

export const LayoutSelectorPanel: React.FC<LayoutSelectorPanelProps> = ({
  isOpen,
  onClose,
  slide,
  conversationId: _conversationId,
  layouts,
  onLayoutSelect,
}) => {
  const [selectedLayoutId, setSelectedLayoutId] = useState(slide.layoutId);
  const [otherTemplates, setOtherTemplates] = useState<ParentTemplate[]>([]);
  const [loadingOtherTemplates, setLoadingOtherTemplates] = useState(false);
  const [expandedTemplateIds, setExpandedTemplateIds] = useState<Set<string>>(new Set());

  // Find current layout - first check current template, then other templates
  const currentLayout = layouts.find(l => l.layoutId === slide.layoutId);

  // Get thumbnail from: slide.layoutThumbnail (set when selected), or currentLayout, or search other templates
  const currentThumbnail = slide.layoutThumbnail || currentLayout?.thumbnail || (() => {
    // Search in other templates if not found
    for (const template of otherTemplates) {
      const layout = template.layouts.find(l => l.layoutId === slide.layoutId);
      if (layout?.previewUrl) return layout.previewUrl;
    }
    return undefined;
  })();

  // Extract current template's parentId from the slide's layoutId
  // Format: "parentId/layoutId" (e.g., "teal-pitch/layout_01")
  const currentTemplateId = slide.layoutId?.split('/')[0] || '';

  // Update selected when slide changes
  useEffect(() => {
    setSelectedLayoutId(slide.layoutId);
  }, [slide.layoutId]);

  // Fetch other templates when panel opens
  useEffect(() => {
    if (isOpen && otherTemplates.length === 0) {
      fetchOtherTemplates();
    }
  }, [isOpen]);

  const fetchOtherTemplates = async () => {
    setLoadingOtherTemplates(true);
    try {
      const response = await templateAPI.listPublicTemplates();
      if (response.success) {
        // Filter to only parent templates and exclude current template
        const parents = response.templates.filter(
          (t): t is ParentTemplate => t.type === 'parent' && t.id !== currentTemplateId
        );
        setOtherTemplates(parents);
      }
    } catch (error) {
      console.error('Failed to fetch other templates:', error);
    } finally {
      setLoadingOtherTemplates(false);
    }
  };

  const handleSelect = (layout: TemplateLayout) => {
    setSelectedLayoutId(layout.layoutId);
    onLayoutSelect(layout.layoutId, layout.name, layout.thumbnail);
  };

  const handleSelectFromOtherTemplate = (
    layoutId: string,
    layoutName: string,
    thumbnail?: string
  ) => {
    setSelectedLayoutId(layoutId);
    onLayoutSelect(layoutId, layoutName, thumbnail);
  };

  const toggleTemplateExpansion = (templateId: string) => {
    setExpandedTemplateIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          "fixed right-0 top-0 bottom-0 w-[420px] z-50",
          "bg-white dark:bg-zinc-950",
          "border-l border-gray-200 dark:border-white/[0.08]",
          "flex flex-col",
          "animate-in slide-in-from-right duration-300"
        )}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-200 dark:border-white/[0.08]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center">
                <LayoutGrid className="w-4 h-4 text-gray-500 dark:text-white/60" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Select Layout</h2>
                <p className="text-xs text-gray-500 dark:text-white/40">Slide {slide.order}: {slide.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Current Selection */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02]">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-white/30 font-medium mb-3">
            Current Layout
          </p>
          <div className="flex items-start gap-4">
            <div className="w-32 aspect-video rounded-lg overflow-hidden border-2 border-primary ring-2 ring-primary/20">
              {currentThumbnail ? (
                <img
                  src={currentThumbnail}
                  alt={slide.layoutName}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-gray-100 dark:bg-zinc-800 flex items-center justify-center">
                  <span className="text-xs text-gray-400 dark:text-white/40">{slide.layoutName}</span>
                </div>
              )}
            </div>
            <div className="flex-1 pt-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{slide.layoutName}</span>
                {!slide.userModified && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-[10px] text-primary font-medium">
                    <Sparkles className="w-3 h-3" />
                    AI Pick
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-white/40 mt-1">
                Click any layout below to change
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable Layout Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Current Template Layouts */}
          <div className="px-5 py-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-white/30 font-medium mb-4">
              Current Template ({layouts.length} layouts)
            </p>

            {layouts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Loader2 className="w-5 h-5 animate-spin text-gray-300 dark:text-white/20 mb-2" />
                <p className="text-xs text-gray-500 dark:text-white/40">Loading layouts...</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {layouts.map((layout) => {
                  const isSelected = layout.layoutId === selectedLayoutId;
                  const isCurrent = layout.layoutId === slide.layoutId;

                  return (
                    <button
                      key={layout.id}
                      onClick={() => handleSelect(layout)}
                      className={cn(
                        "group relative flex flex-col rounded-xl overflow-hidden",
                        "border-2 transition-all duration-200",
                        "focus:outline-none focus:ring-2 focus:ring-primary/50",
                        isSelected
                          ? "border-primary ring-2 ring-primary/20 bg-accent dark:bg-primary/5"
                          : "border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                      )}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-video bg-gray-100 dark:bg-zinc-900 relative">
                        {layout.thumbnail ? (
                          <img
                            src={layout.thumbnail}
                            alt={layout.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <LayoutGrid className="w-6 h-6 text-gray-300 dark:text-white/20" />
                          </div>
                        )}

                        {/* Selected checkmark */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-lg">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}

                        {/* Hover overlay */}
                        {!isSelected && (
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              Select
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Label */}
                      <div className="px-2 py-2 bg-gray-50 dark:bg-black/20">
                        <div className="flex items-center justify-between">
                          <span className={cn(
                            "text-[11px] font-medium truncate",
                            isSelected ? "text-gray-900 dark:text-white" : "text-gray-600 dark:text-white/70"
                          )}>
                            {layout.name}
                          </span>
                          {isCurrent && !isSelected && (
                            <span className="text-[9px] text-gray-400 dark:text-white/30 font-medium">Current</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Other Templates Section */}
          <div className="px-5 py-4 border-t border-gray-200 dark:border-white/[0.08]">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-white/30 font-medium mb-4">
              Other Templates
            </p>

            {loadingOtherTemplates ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Loader2 className="w-5 h-5 animate-spin text-gray-300 dark:text-white/20 mb-2" />
                <p className="text-xs text-gray-500 dark:text-white/40">Loading templates...</p>
              </div>
            ) : otherTemplates.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-white/40 text-center py-4">
                No other templates available
              </p>
            ) : (
              <div className="space-y-2">
                {otherTemplates.map((template) => {
                  const isExpanded = expandedTemplateIds.has(template.id);

                  return (
                    <div key={template.id} className="rounded-xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
                      {/* Template Header */}
                      <button
                        onClick={() => toggleTemplateExpansion(template.id)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3",
                          "hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors",
                          "text-left"
                        )}
                      >
                        {/* Template thumbnail */}
                        <div className="w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden bg-gray-100 dark:bg-zinc-800">
                          {template.previewImage ? (
                            <img
                              src={template.previewImage}
                              alt={template.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div
                              className="w-full h-full"
                              style={{
                                background: template.colorPalette
                                  ? `linear-gradient(135deg, ${template.colorPalette.primary || '#6366f1'}, ${template.colorPalette.secondary || '#8b5cf6'})`
                                  : 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                              }}
                            />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {template.name}
                          </p>
                          <p className="text-[11px] text-gray-500 dark:text-white/40">
                            {template.layouts.length} layouts
                          </p>
                        </div>

                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-400 dark:text-white/40 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400 dark:text-white/40 flex-shrink-0" />
                        )}
                      </button>

                      {/* Expanded Layouts */}
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-1 bg-gray-50 dark:bg-white/[0.02] border-t border-gray-100 dark:border-white/[0.04]">
                          <div className="grid grid-cols-3 gap-2">
                            {template.layouts.map((layout) => {
                              const isSelected = layout.id === selectedLayoutId;

                              return (
                                <button
                                  key={layout.id}
                                  onClick={() => handleSelectFromOtherTemplate(
                                    layout.id,
                                    layout.name,
                                    layout.previewUrl
                                  )}
                                  className={cn(
                                    "group relative flex flex-col rounded-lg overflow-hidden",
                                    "border transition-all duration-200",
                                    "focus:outline-none focus:ring-2 focus:ring-primary/50",
                                    isSelected
                                      ? "border-primary ring-1 ring-primary/20"
                                      : "border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20"
                                  )}
                                >
                                  {/* Thumbnail */}
                                  <div className="aspect-video bg-gray-100 dark:bg-zinc-900 relative">
                                    {layout.previewUrl ? (
                                      <img
                                        src={layout.previewUrl}
                                        alt={layout.name}
                                        className="w-full h-full object-contain"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <LayoutGrid className="w-4 h-4 text-gray-300 dark:text-white/20" />
                                      </div>
                                    )}

                                    {/* Selected checkmark */}
                                    {isSelected && (
                                      <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                        <Check className="w-2.5 h-2.5 text-white" />
                                      </div>
                                    )}
                                  </div>

                                  {/* Label */}
                                  <div className="px-1.5 py-1 bg-white dark:bg-black/20">
                                    <span className="text-[10px] font-medium text-gray-600 dark:text-white/70 truncate block">
                                      {layout.name}
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-4 border-t border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02]">
          <button
            onClick={onClose}
            className={cn(
              "w-full h-10 rounded-lg",
              "bg-primary text-primary-foreground font-medium text-sm",
              "hover:bg-primary/90 transition-colors",
              "flex items-center justify-center gap-2"
            )}
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
};
